import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import MatrixHeading from "./MatrixHeading";
import "./Event.css";

const Event = () => {
    // Define variables for the years
    const currentYear = 2026;
    const previousYear = 2025;

    // State management for active year and content
    // Default to the previous year (2025) since 2026 events are TBA.
    const [activeYear, setActiveYear] = useState(previousYear.toString());
    const [activeContent, setActiveContent] = useState("content1");

    /* MatrixHeading reveal — fires when the Events section enters
       the viewport; resets when it leaves so it replays on re-entry. */
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold: 0.25 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    /* Animate the green selector pill behind the active year tab,
       identical to the Board section's behaviour. */
    useEffect(() => {
        const tabs = document.querySelector("#Events .tabs");
        const selector = document.querySelector("#Events .selector");
        if (!tabs || !selector) return undefined;

        // Position the pill behind whichever tab is active. Recomputed
        // after fonts load / resize so it doesn't sit out of bounds on
        // first paint (Audiowide loads after the initial layout).
        const positionSelector = () => {
            const activeItem = tabs.querySelector(".active");
            if (activeItem) {
                selector.style.left = `${activeItem.offsetLeft}px`;
                selector.style.width = `${activeItem.offsetWidth}px`;
            }
        };

        positionSelector();
        // run again after layout settles + once webfonts are ready
        requestAnimationFrame(positionSelector);
        const t = setTimeout(positionSelector, 250);
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(positionSelector);
        }

        const handleTabSelector = (event) => {
            const item = event.target;
            if (item.tagName === "A") {
                selector.style.left = `${item.offsetLeft}px`;
                selector.style.width = `${item.offsetWidth}px`;
            }
        };
        tabs.addEventListener("click", handleTabSelector);
        window.addEventListener("resize", positionSelector);
        return () => {
            clearTimeout(t);
            tabs.removeEventListener("click", handleTabSelector);
            window.removeEventListener("resize", positionSelector);
        };
    }, [activeYear]);

    // Handler to change the active year
    const handleYearChange = (year) => {
        setActiveYear(year);
        // Reset to the first content item when the year changes
        setActiveContent("content1");
    };

    // Handler to change the active content within a year
    const handleContentChange = (contentId) => {
        setActiveContent(contentId);
    };

    // Data structure for events to avoid repetition in JSX
    const eventsData = {
        // 2026 — five events. Cultrix is filled; the rest get their
        // descriptions as they're provided.
        [currentYear]: [
            { id: "content1", name: "Yantra – Culturix", img: "events/2026/cultrix.jpeg", description: "Yantra – Culturix, organized by IEEE TEMS, is a Tech for Cultural Impact Challenge where tradition inspires innovation and technology preserves legacy. Participants build Web3 solutions for social responsibility, drawing inspiration from culture and community to design and pitch innovative, globally relevant projects. The initiative transforms cultural ideas into impactful solutions — blending creativity, heritage, and cutting-edge technology into one powerful experience. Build with purpose, innovate with culture, and create impact that lasts." },
            { id: "content2", name: "Beyond ChatGPT", img: "events/2026/chatgpt.jpeg", description: "Beyond ChatGPT explored the next frontier of artificial intelligence — the Agent Era. Led by guest speaker Prateek Gupta, Founder & CEO of CustomerGlu and an AI Engineering Leader (Alumni 2016), this masterclass dived into building Autonomous AI Agents that can plan, research, and execute tasks independently. Attendees discovered how AI is moving beyond simple prompts toward fully autonomous intelligence — the shift from generative AI that answers questions to agentic AI that acts like a digital employee, planning the steps, using tools, and completing work on its own." },
            { id: "content3", name: "Gemini API & Streamlit Essentials", img: "events/2026/gemini.jpeg", description: "Gemini API & Streamlit Essentials was a hands-on session on building AI-powered applications using the Gemini API and Streamlit. Participants explored generative AI integration, rapid prototyping, and creative web app development through practical learning and interactive demos. The session helped attendees turn ideas into intelligent applications — diving into the world of AI to learn how to build creative, interactive web experiences from the ground up." },
            { id: "content4", name: "GitHub Basics", img: "events/2026/git.jpeg", description: "Code. Commit. Conquer — Build Your Portfolio on GitHub was a hands-on session on mastering Git and GitHub, where code meets collaboration. Participants kickstarted their coding journey, learning to manage projects from their first commit to final deployment while building a strong developer portfolio along the way. The session showed how ideas turn into impactful, well-organized code, equipping attendees with the essential version-control and collaboration skills every developer needs." },
            { id: "content5", name: "Stranger Nights", img: "events/2026/stranger.jpeg", description: "Stranger Nights transported participants straight into the Upside Down of 1984 — the Gate has opened, signals are failing, and Hawkins needs saving. This immersive, Stranger Things-themed challenge pitted synchronized squads against a gauntlet of high-stakes rounds, from 'Head's Up' and 'Capture Gates' to 'Static Headphone', 'Mind Flayer', and a final showdown, 'Henry Creel'. Teams raced against time to decode the unknown, climb the leaderboard, and seal the Gate before it was too late. With clever puzzles, frequency-bending tasks, and relentless teamwork, it was a thrilling test of wit, coordination, and nerve. Enter the dimension. Trust your squad. Save Hawkins." }
        ],
        [previousYear]: [
            { id: "content1", name: "SDG Conclave", img: "events/2025/sdgconclave.jpeg", description: "An enlightening session that delved into the United Nations' 17 Sustainable Development Goals. This event served as a catalyst for change, encouraging participants to think critically about global challenges and innovate for a sustainable future. It was a powerful call to action, inspiring attendees to harness their skills and ideas to contribute towards a better, more equitable world for all." },
            { id: "content2", name: "Squid Survivors", img: "events/2025/squid.png", description: "During Riviera 2025, we brought the global phenomenon to campus with \"Squid Survivors.\" This thrilling event challenged participants with a series of high-stakes games inspired by the popular series. Contestants tested their wit, agility, and nerve in a friendly but competitive atmosphere, all vying for the ultimate prize. It was an unforgettable experience filled with suspense, excitement, and teamwork, making it a standout attraction of the fest." },
            { id: "content3", name: "Bouncing Castle", img: "events/2025/bouncin.png", description: "We added a dose of pure fun to Riviera 2025 with our very own Bouncing Castle. This pay-to-play attraction offered a chance for everyone to let loose, jump around, and relive some carefree childhood moments amidst the hustle and bustle of the fest. It was a simple, joyous escape that brought smiles and laughter to many, proving to be a popular spot for students to relax and have a great time with friends." },
            { id: "content4", name: "HackXpertise 2.0", img: "events/2025/hackx2.png", description: "Building on the success of its predecessors, HackXpertise 2.0 returned as our flagship innovation marathon. This year, we enhanced the experience with preliminary sessions on Git for effective version control and a tutorial on leveraging the latest AI tools. Following the workshops, participants dove into a hackathon and designathon, working tirelessly in teams to develop groundbreaking solutions. The event was a testament to the creativity and technical skill within our community." },
            { id: "content5", name: "Alumni Talks", img: "events/2025/alumini.jpeg", description: "An inspiring session connecting current members with our accomplished alumni. In a warm and interactive setting, our seniors shared their career journeys, industry experiences, and the lessons they learned along the way. From navigating academics and internships to breaking into the tech industry, they offered candid advice and answered questions from eager students. The event strengthened our community bonds and gave members valuable insight and motivation to chart their own paths toward a successful future." },
            { id: "content6", name: "HP Dreams Unlocked", img: "events/2025/hp.png", description: "In an exciting collaboration with HP, we hosted \"HP Dreams Unlocked,\" a dynamic on-campus event. The day was packed with an inspiring tech workshop, fun creative challenges, and a high-speed Live Pitch Challenge where innovators presented their ideas in just 60 seconds. Participants engaged with cutting-edge technology and had the chance to win exclusive HP giveaways, with one lucky winner even taking home a brand-new AI-powered HP Omnibook laptop." },
            { id: "content7", name: "Code Rush 3.0", img: "events/2025/coderush3.png", description: "The ultimate test of speed, skill, and teamwork, Code Rush 3.0, made its triumphant return. This offline coding competition saw teams battle it out in our signature relay-race format. Each member tackled a segment of a complex problem before passing it to their teammate, creating an electrifying and collaborative atmosphere. The event pushed participants to code efficiently under pressure, celebrating both individual talent and the power of a well-coordinated team." },
            { id: "content8", name: "AI & Data Science Foundation", img: "events/2025/aids.jpeg", description: "A hands-on foundation session designed to introduce members to the exciting world of Artificial Intelligence and Data Science. Participants explored core concepts spanning data handling, machine learning fundamentals, and real-world AI applications. Through practical demonstrations and guided exercises, attendees built an intuitive understanding of how intelligent systems learn from data. The event laid a strong groundwork for beginners, sparking curiosity and equipping members with the essential skills to confidently begin their journey into AI and Data Science." }
        ]
    };

    // Compute the currently selected event so its name + description
    // can appear on the LEFT (below the section title), mirroring the
    // About Us layout.
    const activeYearData =
        activeYear === currentYear.toString()
            ? eventsData[currentYear]
            : eventsData[previousYear];
    const activeEvent =
        activeYearData.find((e) => e.id === activeContent) || activeYearData[0];

    // Renders just the visual UI (year-ring + big card) for a year.
    // The text description block is no longer rendered here — it has
    // moved to the LEFT column so we don't duplicate it.
    const renderYearEvents = (year) => {
        const yearData = eventsData[year] || [];
        return (
            <div
                className={`active-year evnentContent ${activeYear === year.toString() ? "" : "hidden"}`}
                id={`${year}events`}
            >
                <div className="container1">
                    <div className="event-icon">
                        {yearData.map((event, index) => (
                            <div
                                key={event.id}
                                className={`imgBx ${activeContent === event.id ? "active" : ""}`}
                                style={{ "--i": index + 1, "--count": yearData.length }}
                                data-id={event.id}
                                onClick={() => handleContentChange(event.id)}
                            >
                                <img src={event.img} alt={`${event.name}, IEEE TEMS`} />
                            </div>
                        ))}
                    </div>

                    <div className="content1">
                        {yearData.map(event => (
                             <div
                                key={event.id}
                                className={`contentBx ${activeContent === event.id ? "active" : ""}`}
                                id={event.id}
                            >
                                <div className="card">
                                    <div className="imgBx2">
                                        <img src={event.img} alt={`${event.name}, IEEE TEMS`} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section id="Events" ref={sectionRef}>
            <div className="evt-split">
                {/* LEFT — title block + active event info (mirrors About Us) */}
                <div className="evt-left">

                    <h2 className="evt-main-heading">
                        <MatrixHeading text="Events" active={isVisible} />
                    </h2>

                    <p className="evt-tagline">
                        <span className="evt-tagline-row">MILESTONES</span>
                        <span className="evt-tagline-row">MOMENTS</span>
                    </p>

                    <div className="evt-heading-line" />

                    {/* Active event details — fades in / out with selection */}
                    <div className="evt-content-wrap">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={`${activeYear}-${activeContent}`}
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                                className="evt-content"
                            >
                                <h3 className="evt-event-name">{activeEvent.name}</h3>
                                <p className="evt-event-desc">{activeEvent.description}</p>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <p className="evt-mission-text">
                        A snapshot of 2025 and 2026 — moments that shaped us.
                    </p>
                </div>

                {/* RIGHT — existing event UI: year tabs + ring + main card */}
                <div className="evt-right">
                    <div className="wrapper" id="content-desktop">
                        <nav className="tabs">
                            <div className="selector"></div>
                            <a
                                href="#Event"
                                id={currentYear.toString()}
                                className={activeYear === currentYear.toString() ? "active no-border-on-link" : "no-border-on-link"}
                                onClick={() => handleYearChange(currentYear.toString())}
                            >
                                {currentYear}
                            </a>
                            <a
                                href="#Event"
                                id={previousYear.toString()}
                                className={activeYear === previousYear.toString() ? "active no-border-on-link" : "no-border-on-link"}
                                onClick={() => handleYearChange(previousYear.toString())}
                            >
                                {previousYear}
                            </a>
                        </nav>
                    </div>

                    <section className="yo" style={{ marginTop: "30px" }}>
                        {renderYearEvents(currentYear)}
                        {renderYearEvents(previousYear)}
                    </section>
                </div>
            </div>
        </section>
    );
};

export default Event;
